import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoldPrice } from '@/hooks/useGoldPrice';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/base/Toast';
import { useTradingStatus } from '@/hooks/useTradingStatus';
import {
  goldDepositOptions,
  silverDepositOptions,
  lockTerms,
  tradeProducts,
  riskDisclaimerContent,
  expirationRules,
  calcStorageFee,
  storageFeeConfig,
  calcCraftFee,
  craftFeeConfig,
  parseLockDays,
} from '@/mocks/lockPrice';

const steps = [
  { step: 1, label: '确认信息' },
  { step: 2, label: '全额付款' },
  { step: 3, label: '预约成功' },
];

export default function LockPricePage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { getPrice, getBidPrice, getDirection, getChange, formatPrice, simulateWave, stopWave, isLive, isPriceLive } = useGoldPrice();

  const [weight, setWeight] = useState(500);
  const [weightInput, setWeightInput] = useState('500');
  const [selectedRatio, setSelectedRatio] = useState(0.06);
  const [agreed, setAgreed] = useState(false);
  const [pressedSubmit, setPressedSubmit] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(tradeProducts[0]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [orderDirection, setOrderDirection] = useState<'IN' | 'OUT'>('IN');
  const sliderRef = useRef<HTMLDivElement>(null);

  // 不再启动任何模拟波动，完全依赖真实行情
  useEffect(() => {
    return () => stopWave();
  }, [stopWave]);

  // 价格闪烁动画状态
  const [priceFlash, setPriceFlash] = useState<'up' | 'down' | null>(null);
  const prevPriceRef = useRef<number>(0);
  const [lastUpdateTime, setLastUpdateTime] = useState<string>('');
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const [canConfirmRisk, setCanConfirmRisk] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // 检查是否已阅读过风险提示书
  useEffect(() => {
    const hasRead = localStorage.getItem('risk_disclaimer_read') === 'true';
    if (hasRead) {
      setAgreed(true);
    }
  }, []);

  // Order confirm modal
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 实时金价 — 下单锁价分方向定价：买入按销售价（ask），卖出按回购价（bid）
  const liveGoldAsk = getPrice('黄金') || 950.0;
  const liveGoldBid = getBidPrice('黄金') || 949.0;
  const liveSilverAsk = getPrice('白银') || 8.15;
  const liveSilverBid = getBidPrice('白银') || 8.10;
  const liveKiloBarAsk = getPrice('公斤条') || 950.0;
  const liveKiloBarBid = getBidPrice('公斤条') || 949.0;
  // 买入按销售价，卖出按回购价
  const currentPrice = selectedProduct.isKiloBar
    ? (orderDirection === 'IN' ? liveKiloBarAsk : liveKiloBarBid)
    : selectedProduct.type === 'silver'
      ? (orderDirection === 'IN' ? liveSilverAsk : liveSilverBid)
      : (orderDirection === 'IN' ? liveGoldAsk : liveGoldBid);

  // 检测价格变化，触发闪烁
  useEffect(() => {
    const rawLabel = selectedProduct.isKiloBar ? '公斤条' : selectedProduct.type === 'silver' ? '白银' : '黄金';
    const direction = getDirection(rawLabel);
    const change = getChange(rawLabel);

    if (prevPriceRef.current > 0 && currentPrice !== prevPriceRef.current) {
      if (direction === 'up') setPriceFlash('up');
      else if (direction === 'down') setPriceFlash('down');

      const now = new Date();
      setLastUpdateTime(
        `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`
      );

      // 1秒后清除闪烁
      const t = setTimeout(() => setPriceFlash(null), 1000);
      return () => clearTimeout(t);
    }
    prevPriceRef.current = currentPrice;
  }, [currentPrice, getDirection, getChange, selectedProduct.type]);

  // 涨跌显示
  const rawLabel = selectedProduct.isKiloBar ? '公斤条' : selectedProduct.type === 'silver' ? '白银' : '黄金';
  const direction = getDirection(rawLabel);
  const changePct = getChange(rawLabel);
  const totalAmount = currentPrice * weight;
  const depositAmount = totalAmount * selectedRatio;

  // 仓管费计算
  const storageFee = calcStorageFee(selectedProduct.type, selectedProduct.isKiloBar || false, weight);
  const storageFeeDisplay = selectedProduct.isKiloBar
    ? storageFeeConfig.kiloBar.display
    : selectedProduct.type === 'silver'
      ? storageFeeConfig.silver.display
      : storageFeeConfig.gold.display;

  // 工费计算
  const craftFee = calcCraftFee(selectedProduct.type, selectedProduct.isKiloBar || false, weight);
  const craftFeeDisplay = selectedProduct.isKiloBar
    ? craftFeeConfig.kiloBar.display
    : selectedProduct.type === 'silver'
      ? craftFeeConfig.silver.display
      : craftFeeConfig.gold.display;
  const totalDeduct = depositAmount + storageFee + craftFee;

  const currentOptions = selectedProduct.type === 'silver' ? silverDepositOptions : goldDepositOptions;
  const selectedOption = currentOptions.find((o) => o.value === selectedRatio) || currentOptions[0];
  const currentLockPeriod = selectedOption.lockPeriod;

  // Slider: compute index-based percentage for visual positioning
  // 限制在 8%-92% 范围内，防止 thumb 和 tooltip 在两端溢出卡片
  const optionIndex = currentOptions.findIndex((o) => o.value === selectedRatio);
  const maxIndex = currentOptions.length - 1;
  const sliderPercent = maxIndex > 0
    ? (optionIndex / maxIndex) * 84 + 8
    : 50;

  const handleMinus = useCallback(() => {
    const newVal = Math.max(100, weight - 100);
    setWeight(newVal);
    setWeightInput(String(newVal));
  }, [weight]);
  const handlePlus = useCallback(() => {
    const newVal = weight + 100;
    setWeight(newVal);
    setWeightInput(String(newVal));
  }, [weight]);

  const handleWeightChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setWeightInput(raw);
    const v = Number(raw);
    if (raw !== '' && !Number.isNaN(v) && v >= 100) {
      setWeight(v);
    }
  }, []);

  const handleWeightBlur = useCallback(() => {
    const v = Number(weightInput);
    if (weightInput === '' || Number.isNaN(v) || v < 100) {
      setWeightInput('100');
      setWeight(100);
    } else {
      setWeightInput(String(v));
    }
  }, [weightInput]);

  const handleProductSelect = useCallback((product: typeof tradeProducts[0]) => {
    setSelectedProduct(product);
    setDropdownOpen(false);
    // 公斤条默认选 10%，黄金/白银默认 6%
    const defaultValue = product.isKiloBar ? 0.10 : 0.06;
    setSelectedRatio(defaultValue);
  }, []);

  // Slider interaction: snap to nearest option index
  const updateSliderFromClientX = useCallback((clientX: number) => {
    const el = sliderRef.current;
    if (!el || maxIndex <= 0) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
    const percent = x / rect.width;
    const idx = Math.round(percent * maxIndex);
    const clampedIdx = Math.max(0, Math.min(maxIndex, idx));
    setSelectedRatio(currentOptions[clampedIdx].value);
  }, [currentOptions, maxIndex]);

  const handleSliderClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    updateSliderFromClientX(e.clientX);
  }, [updateSliderFromClientX]);

  const handleSliderStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updateSliderFromClientX(e.clientX);

    const onMove = (ev: PointerEvent) => {
      updateSliderFromClientX(ev.clientX);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [updateSliderFromClientX]);

  const handleOpenRiskModal = useCallback(() => {
    setRiskModalOpen(true);
    setReadProgress(0);
    setCanConfirmRisk(false);
  }, []);

  const handleCloseRiskModal = useCallback(() => {
    setRiskModalOpen(false);
  }, []);

  const handleConfirmRiskRead = useCallback(() => {
    if (!canConfirmRisk) return;
    setAgreed(true);
    setRiskModalOpen(false);
    localStorage.setItem('risk_disclaimer_read', 'true');
  }, [canConfirmRisk]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const maxScroll = scrollHeight - clientHeight;
    if (maxScroll <= 0) {
      setReadProgress(100);
      setCanConfirmRisk(true);
      return;
    }
    const progress = Math.min((scrollTop / maxScroll) * 100, 100);
    setReadProgress(progress);
    setCanConfirmRisk(progress >= 95);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !riskModalOpen) return;
    el.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();
    return () => el.removeEventListener('scroll', handleScroll);
  }, [riskModalOpen, handleScroll]);

  const handleDirectionClick = useCallback((dir: 'IN' | 'OUT') => {
    if (!agreed) {
      handleOpenRiskModal();
      return;
    }
    if (!isLive || !isPriceLive(rawLabel)) {
      showToast('行情未连接，价格异常，已暂停下单', 'error');
      return;
    }
    setOrderDirection(dir);
    setConfirmModalOpen(true);
  }, [agreed, isLive, isPriceLive, rawLabel, showToast, handleOpenRiskModal]);

  const handleConfirmOrder = useCallback(async () => {
    if (!agreed || submitting) return;
    if (!isLive || !isPriceLive(rawLabel)) {
      showToast('行情未连接，价格异常，已暂停下单', 'error');
      return;
    }
    setSubmitting(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        showToast('请先登录', 'error');
        navigate('/login');
        return;
      }

      // 并行查询：实名认证状态 + 账户余额（不再做价格二次确认——
      // 页面实时价本身来自实时行情流，且下单前已有行情未连接/暂停交易两道保护）
      const [profileRes, summaryRes] = await Promise.all([
        supabase.from('profiles').select('verified').eq('id', user.id).maybeSingle(),
        supabase.from('account_summary').select('*').eq('user_id', user.id).maybeSingle(),
      ]);

      const profileData = profileRes.data;
      const summary = summaryRes.data;

      if (!profileData?.verified) {
        showToast('请先完成实名认证后再交易', 'error');
        navigate('/profile/verify');
        return;
      }

      const currentAvailable = Number(summary?.available_margin) || 0;
      if (currentAvailable < totalDeduct) {
        showToast(`可用余额不足，需定金+仓管费+工费 ¥${totalDeduct.toFixed(2)}，请先入金`, 'error');
        return;
      }

      setConfirmModalOpen(false);

      const orderId = `ORD${Date.now()}`;
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const createTime = `${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
      const remainingAmount = totalAmount - depositAmount;

      // 计算锁价到期时间
      const lockDays = parseLockDays(selectedOption.lockPeriod);
      const lockExpireAt = new Date(now.getTime() + lockDays * 24 * 60 * 60 * 1000).toISOString();

      // 并行写入：订单 + 定金支付单 + 尾款支付单 + 账户汇总
      const writeTasks = [
        supabase.from('orders').insert({
          id: orderId,
          user_id: user.id,
          name: selectedProduct.name,
          weight,
          direction: orderDirection,
          open_price: currentPrice,
          current_price: currentPrice,
          profit: 0,
          original_margin: depositAmount,
          current_margin: depositAmount,
          deferred_fee: 0,
          risk_level: 0,
          paid_in: 0,
          paid_out: 0,
          status: 'open',
          create_time: createTime,
          margin_ratio: selectedRatio,
          liquidation_line: 0.20,
          total_amount: totalAmount,
          storage_fee: storageFee,
          storage_fee_charged: storageFee,
          craft_fee: craftFee,
          lock_expire_at: lockExpireAt,
        }),
        supabase.from('payments').insert({
          id: `${orderId}_D`,
          user_id: user.id,
          order_id: orderId,
          product: selectedProduct.name,
          amount: depositAmount,
          status: 'paid',
          payment_method: '余额支付',
          create_time: createTime,
        }),
        ...(craftFee > 0
          ? [
              supabase.from('payments').insert({
                id: `${orderId}_CF`,
                user_id: user.id,
                order_id: orderId,
                product: selectedProduct.name,
                amount: craftFee,
                status: 'paid',
                payment_method: '工费',
                create_time: createTime,
              }),
            ]
          : []),
        ...(storageFee > 0
          ? [
              supabase.from('payments').insert({
                id: `${orderId}_SF0`,
                user_id: user.id,
                order_id: orderId,
                product: selectedProduct.name,
                amount: storageFee,
                status: 'paid',
                payment_method: '仓管费',
                create_time: createTime,
              }),
            ]
          : []),
        ...(remainingAmount > 0
          ? [
              supabase.from('payments').insert({
                id: `${orderId}_B`,
                user_id: user.id,
                order_id: orderId,
                product: selectedProduct.name,
                amount: remainingAmount,
                status: 'pending',
                payment_method: null,
                create_time: createTime,
              }),
            ]
          : []),
        summary
          ? supabase
              .from('account_summary')
              .update({
                available_margin: Math.max(0, currentAvailable - totalDeduct),
                used_margin: (Number(summary.used_margin) || 0) + depositAmount,
                long_weight: (Number(summary.long_weight) || 0) + (orderDirection === 'IN' ? weight : 0),
                short_weight: (Number(summary.short_weight) || 0) + (orderDirection === 'OUT' ? weight : 0),
                net_weight: (Number(summary.net_weight) || 0) + (orderDirection === 'IN' ? weight : -weight),
              })
              .eq('user_id', user.id)
          : supabase.from('account_summary').insert({
              user_id: user.id,
              available_margin: Math.max(0, -totalDeduct),
              used_margin: depositAmount,
              floating_profit: 0,
              product_name: selectedProduct.name,
              long_weight: orderDirection === 'IN' ? weight : 0,
              short_weight: orderDirection === 'OUT' ? weight : 0,
              net_weight: orderDirection === 'IN' ? weight : -weight,
            }),
      ];

      const writeResults = await Promise.all(writeTasks);
      const writeError = writeResults.find((r) => r.error);
      if (writeError) {
        console.error('[LockPrice] 写入失败:', writeError.error);
        showToast('下单失败：数据写入异常，请检查网络或后端连接状态', 'error');
        return;
      }

      // 发送订单通知邮件（不阻塞下单）
      supabase.functions
        .invoke('send-order-email', {
          body: {
            orderId,
            product: selectedProduct.name,
            weight,
            price: currentPrice,
            totalAmount,
            depositAmount,
            storageFee,
            craftFee,
            createTime,
            direction: orderDirection,
          },
        })
        .then((res) => {
          const data = res.data as { success?: boolean; error?: string } | null;
          if (res.error) {
            console.error('[order-email] failed:', res.error);
          } else if (data && data.success === false) {
            console.error('[order-email] failed:', data.error);
          } else {
            console.log('[order-email] success', data);
          }
        })
        .catch((err) => {
          console.error('[order-email] failed:', err);
        });

      showToast('下单成功', 'success');
      navigate('/orders');
    } catch (err: unknown) {
      console.error('[LockPrice] 下单异常:', err);
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('ECONNREFUSED') || msg.includes('CORS') || msg.includes('offline')) {
        showToast('后端连接失败，请确保 Supabase 已连接后再试', 'error');
      } else {
        showToast('下单异常：' + msg, 'error');
      }
    } finally {
      setSubmitting(false);
    }
  }, [navigate, selectedProduct, depositAmount, storageFee, craftFee, totalDeduct, currentPrice, weight, agreed, showToast, submitting, totalAmount, selectedRatio, isLive, isPriceLive, rawLabel, orderDirection]);

  const [showBottomBar, setShowBottomBar] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const { status: tradingStatus } = useTradingStatus();

  // 品类暂停检查
  const currentCategoryKey = selectedProduct.isKiloBar
    ? 'kiloBar'
    : selectedProduct.type;
  const isCategoryPaused = tradingStatus?.pausedCategories?.includes(currentCategoryKey) || false;

  // 行情未连接（价格是默认值）或休市/暂停时禁止下单，防止用错误价格成交
  const orderBlocked =
    !isLive ||
    !isPriceLive(rawLabel) ||
    (!!tradingStatus && tradingStatus.enabled && !tradingStatus.isOpen) ||
    isCategoryPaused;

  useEffect(() => {
    const checkScroll = () => {
      const scrollY = window.scrollY;
      const innerHeight = window.innerHeight;
      const scrollHeight = document.documentElement.scrollHeight;
      const nearBottom = scrollY + innerHeight >= scrollHeight - 160;
      setShowBottomBar(nearBottom);
    };
    window.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
    return () => window.removeEventListener('scroll', checkScroll);
  }, []);

  // 价格显示精度
  const priceDigits = selectedProduct.type === 'silver' ? 3 : 2;

  return (
    <div className="min-h-screen bg-brown-50 pb-28" ref={pageRef}>
      {/* Header */}
      <div className="sticky top-0 z-40 bg-brown-950 px-5 py-4 pt-safe flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center cursor-pointer active:scale-90 transition-transform"
        >
          <i className="ri-arrow-left-line text-gold-400 text-lg"></i>
        </button>
        <h1 className="text-gold-400 text-lg font-bold flex-1 text-center whitespace-nowrap">
          预约锁价
        </h1>
        <button className="w-8 h-8 flex items-center justify-center relative cursor-pointer">
          <i className="ri-notification-3-line text-gold-400 text-lg"></i>
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-white text-[9px] flex items-center justify-center font-medium">3</span>
        </button>
      </div>

      {/* Trading Status Banner */}
      {tradingStatus && tradingStatus.enabled && !tradingStatus.isOpen && (
        <div
          className={`mx-3 mt-3 rounded-lg px-3 py-2.5 flex items-center gap-2 animate-fade-up ${
            tradingStatus.manualPaused
              ? 'bg-red-50 border border-red-200'
              : 'bg-amber-50 border border-amber-200'
          }`}
        >
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              tradingStatus.manualPaused ? 'bg-red-400' : 'bg-amber-400'
            }`}
          />
          {tradingStatus.manualPaused ? (
            <p className="text-red-600 text-xs">
              暂停交易中
              {tradingStatus.pauseReason ? ` · ${tradingStatus.pauseReason}` : ''}
            </p>
          ) : (
            <p className="text-amber-700 text-xs">
              当前休市中，距离开盘还有 {tradingStatus.countdown}
              <span className="text-amber-500 ml-1">
                （{tradingStatus.nextOpenTime
                  ? new Date(tradingStatus.nextOpenTime).toLocaleString('zh-CN', {
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: false,
                    }).replace(/\//g, '-')
                  : tradingStatus.openTime?.slice(0, 5)}
                开盘）
              </span>
            </p>
          )}
        </div>
      )}

      {/* 行情未连接提示：禁止下单，防止用默认价格成交 */}
      {!isLive && (
        <div className="mx-3 mt-3 rounded-lg px-3 py-2.5 flex items-center gap-2 animate-fade-up bg-red-50 border border-red-200">
          <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0 animate-pulse" />
          <p className="text-red-600 text-xs">行情未连接，当前显示默认价格，已暂停下单</p>
        </div>
      )}

      {/* 该品种无实时行情提示：即使其它品种有行情，也禁止用兜底价下单 */}
      {isLive && !isPriceLive(rawLabel) && (
        <div className="mx-3 mt-3 rounded-lg px-3 py-2.5 flex items-center gap-2 animate-fade-up bg-red-50 border border-red-200">
          <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0 animate-pulse" />
          <p className="text-red-600 text-xs">{rawLabel} 暂无实时行情，已暂停下单</p>
        </div>
      )}

      {/* 品类暂停提示 */}
      {isCategoryPaused && (
        <div className="mx-3 mt-3 rounded-lg px-3 py-2.5 flex items-center gap-2 animate-fade-up bg-red-50 border border-red-200">
          <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0 animate-pulse" />
          <p className="text-red-600 text-xs">
            {selectedProduct.name} 暂停交易
            {tradingStatus?.pauseReason ? ` · ${tradingStatus.pauseReason}` : ''}
          </p>
        </div>
      )}

      {/* Product Card */}
      <div className="mx-3 mt-4 bg-white rounded-xl shadow-sm overflow-hidden animate-fade-up">
        <div className="px-3.5 pt-4 pb-3">
          <p className="text-gray-400 text-xs tracking-wider">GOLD RESERVATION</p>
          <div className="flex items-center gap-1.5 mt-1">
            <i className="ri-shield-check-line text-amber-500 text-sm"></i>
            <span className="text-amber-600 text-xs font-medium">实物资产保障</span>
          </div>
          <div className="relative mt-2">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 cursor-pointer group"
            >
              <span className="text-brown-900 text-lg font-bold">{selectedProduct.name}</span>
              <span className="text-brown-900/40 text-lg font-bold">{selectedProduct.code}</span>
              <i className={`ri-arrow-down-s-line text-brown-900 text-lg transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setDropdownOpen(false)} />
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 z-40 overflow-hidden max-h-64 overflow-y-auto animate-fade-up">
                  <p className="text-gray-400 text-xs px-4 pt-3 pb-1">选择交易产品</p>
                  {tradeProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      className={`w-full text-left px-4 py-3 flex flex-col cursor-pointer transition-colors duration-150 ${
                        selectedProduct.id === product.id ? 'bg-amber-50' : 'hover:bg-amber-50/60'
                      }`}
                    >
                      <span className={`text-sm font-semibold ${selectedProduct.id === product.id ? 'text-amber-700' : 'text-brown-900'}`}>
                        {product.name}
                      </span>
                      <span className="text-gray-400 text-xs mt-0.5">{product.code}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        <div className="px-4 pb-4">
          <div className="bg-brown-50/60 rounded-lg px-3.5 py-3 flex flex-wrap items-center justify-between gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-brown-600 text-sm whitespace-nowrap">{orderDirection === 'IN' ? '实时买入价' : '实时卖出价'}</span>
              <span className="text-xs px-2 py-0.5 rounded-full border border-amber-300 text-amber-700 bg-amber-50/60 whitespace-nowrap">
                国内现货市场公开报价
              </span>
            </div>
            <div className="text-right flex items-center gap-2 flex-wrap">
              <span
                className={`text-amber-700 text-xl font-bold transition-colors duration-300 ${
                  priceFlash === 'up' ? 'text-red-500 animate-flash-up' : ''
                } ${priceFlash === 'down' ? 'text-green-500 animate-flash-down' : ''}`}
              >
                ¥{currentPrice.toLocaleString('zh-CN', { minimumFractionDigits: priceDigits, maximumFractionDigits: priceDigits })}
              </span>
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                direction === 'up' ? 'bg-red-50 text-red-500' : direction === 'down' ? 'bg-green-50 text-green-500' : 'bg-gray-100 text-gray-500'
              }`}>
                {direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→'}
                {Math.abs(changePct || 0).toFixed(2)}%
              </span>
              <span className="text-gray-400 text-xs ml-0.5 whitespace-nowrap">/{selectedProduct.unit}</span>
            </div>
          </div>
          <div className="flex items-center justify-between mt-5">
            <span className="text-brown-600 text-sm">规格重量</span>
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden h-11">
              <button
                onClick={handleMinus}
                className="w-11 h-full flex items-center justify-center text-gray-400 hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors"
              >
                <span className="text-lg leading-none">-</span>
              </button>
              <div className="w-16 h-full flex items-center justify-center border-l border-r border-gray-200">
                <input
                  type="number"
                  min={100}
                  value={weightInput}
                  onChange={handleWeightChange}
                  onBlur={handleWeightBlur}
                  className="w-full h-full text-center text-brown-900 text-base font-semibold bg-transparent focus:outline-none appearance-none"
                />
              </div>
              <button
                onClick={handlePlus}
                className="w-11 h-full flex items-center justify-center text-gray-400 hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors"
              >
                <i className="ri-add-line text-base"></i>
              </button>
            </div>
          </div>
          <div className="border-t border-dashed border-gray-200 my-5" />
          <div className="flex items-center justify-between">
            <span className="text-brown-900 text-sm font-bold">预约总额</span>
            <span className="text-amber-700 text-xl font-bold">¥{totalAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>
      </div>

      {/* Deposit Ratio Slider */}
      <div className="mx-3 mt-4 bg-white rounded-xl shadow-sm p-3.5 animate-fade-up" style={{ animationDelay: '0.1s' }}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-brown-900 text-sm font-bold">选择定金比例</h3>
          <div className="flex items-center gap-1.5">
            {selectedOption.isRecommended && (
              <span className="bg-gold-500 text-brown-950 text-[9px] px-1.5 py-0.5 rounded font-bold">推荐</span>
            )}
            <span className="text-amber-700 text-sm font-bold">{selectedOption.label}</span>
          </div>
        </div>
        <p className="text-gray-400 text-xs mb-4">锁价{selectedOption.lockPeriod}</p>

        {/* Slider */}
        <div className="relative px-8 pt-10 pb-2 select-none" ref={sliderRef}>
          {/* Tooltip bubble */}
          <div
            className="absolute -top-6 transition-all duration-150 pointer-events-none"
            style={{
              left: `${sliderPercent}%`,
              transform: 'translateX(-50%)',
            }}
          >
            <div className="bg-gold-500 text-brown-950 text-xs font-bold px-3 py-1 rounded-lg whitespace-nowrap shadow-sm">
              {selectedOption.label} · 锁价{selectedOption.lockPeriod}
            </div>
            <div className="w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-l-transparent border-r-transparent border-t-gold-500 mx-auto" />
          </div>

          {/* Track background */}
          <div className="relative h-2.5 bg-gray-200 rounded-full" onClick={handleSliderClick}>
            {/* Filled track */}
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-amber-500 to-gold-500 rounded-full pointer-events-none"
              style={{ width: `${sliderPercent}%` }}
            />
            {/* Thumb */}
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-white border-2 border-gold-500 shadow-md cursor-grab active:cursor-grabbing active:scale-110 hover:scale-110 transition-transform"
              style={{ left: `${sliderPercent}%` }}
              onPointerDown={handleSliderStart}
            />
          </div>

          {/* Ticks & Labels — inline row layout under slider */}
          <div className="flex items-center justify-between mt-5 px-0.5">
            {currentOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedRatio(option.value)}
                className="flex flex-col items-center gap-1.5 cursor-pointer"
              >
                <div className={`w-2 h-2 rounded-full transition-colors ${
                  selectedRatio === option.value ? 'bg-gold-500' : 'bg-gray-300'
                }`} />
                <span className={`text-xs font-bold ${selectedRatio === option.value ? 'text-gold-600' : 'text-gray-400'}`}>
                  {option.label}
                </span>
                <span className={`text-[10px] ${selectedRatio === option.value ? 'text-gold-500' : 'text-gray-400'}`}>
                  锁价{option.lockPeriod}
                </span>
                {option.isRecommended && (
                  <span className="bg-gold-500 text-brown-950 text-[9px] px-1 py-0.5 rounded font-bold mt-0.5">推荐</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Terms Card */}
      <div className="mx-3 mt-4 animate-fade-up" style={{ animationDelay: '0.2s' }}>
        <p className="text-gray-400 text-xs mb-2">费用明细 & 锁价条款</p>
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-3.5 py-3.5 flex items-center justify-between border-b border-gray-50">
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-gray-400 text-lg"></i>
              <span className="text-brown-700 text-sm">价格锁定周期</span>
            </div>
            <span className="text-brown-900 text-sm font-medium">{currentLockPeriod}</span>
          </div>
          <div className="px-3.5 py-3.5 flex items-center justify-between border-b border-gray-50">
            <div className="flex items-center gap-2">
              <i className="ri-bank-card-line text-gray-400 text-lg"></i>
              <span className="text-brown-700 text-sm">应付定金 ({selectedOption.label})</span>
            </div>
            <span className="text-red-500 text-sm font-bold">
              ¥{depositAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="px-3.5 py-3.5 flex items-center justify-between border-b border-gray-50">
            <div className="flex items-center gap-2">
              <i className="ri-archive-line text-gray-400 text-lg"></i>
              <span className="text-brown-700 text-sm">仓管费 ({storageFeeDisplay})</span>
            </div>
            <span className="text-amber-600 text-sm font-bold">
              ¥{storageFee.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="px-3.5 py-3.5 flex items-center justify-between border-b border-gray-50">
            <div className="flex items-center gap-2">
              <i className="ri-hammer-line text-gray-400 text-lg"></i>
              <span className="text-brown-700 text-sm">工费 ({craftFeeDisplay})</span>
            </div>
            <span className="text-amber-600 text-sm font-bold">
              ¥{craftFee.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div className="px-3.5 py-3.5 border-b border-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-list-check-2 text-gray-400 text-lg"></i>
              <span className="text-brown-700 text-sm">到期前可操作</span>
            </div>
            <div className="space-y-1.5 pl-7">
              {expirationRules.map((rule, idx) => (
                <p key={idx} className="text-brown-800 text-xs font-medium">{rule}</p>
              ))}
            </div>
          </div>
          <div className="mx-3.5 mb-4 bg-amber-50 rounded-lg p-3.5 flex items-start gap-2">
            <i className="ri-information-line text-amber-500 text-sm mt-0.5 flex-shrink-0"></i>
            <p className="text-amber-700 text-xs leading-relaxed">{lockTerms.note}</p>
          </div>
        </div>
      </div>

      {/* Agreement */}
      <div className="mx-3 mt-4 mb-4 animate-fade-up" style={{ animationDelay: '0.25s' }}>
        <div className="flex items-start gap-2">
          <div
            className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
              agreed ? 'bg-gold-500 border-gold-500' : 'border-gray-300 bg-white'
            }`}
          >
            {agreed && <i className="ri-check-line text-white text-xs"></i>}
          </div>
          <p className="text-gray-500 text-xs leading-relaxed">
            我已阅读并同意
            <span className="text-gold-600 underline cursor-pointer mx-0.5">《{lockTerms.agreementName}》</span>
            及
            <span
              onClick={handleOpenRiskModal}
              className="text-gold-600 underline cursor-pointer mx-0.5"
            >
              《{lockTerms.riskName}》
            </span>
          </p>
        </div>
      </div>

      {/* Bottom Fixed Bar — slides up only near bottom */}
      <div
        className={`fixed bottom-16 left-0 right-0 z-40 transition-transform duration-300 ease-out ${
          showBottomBar ? 'translate-y-0' : 'translate-y-[120%]'
        }`}
      >
        <div className="mx-3 mb-2 bg-white/95 backdrop-blur rounded-xl shadow-[0_-2px_12px_rgba(0,0,0,0.06)] px-3 py-2.5">
          <div className="flex items-end justify-between mb-2">
            <div>
              <span className="text-gray-400 text-[10px] block mb-0">应付定金 ({selectedOption.label})</span>
              <span className="text-amber-700 text-xl font-bold">
                ¥{depositAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </span>
            </div>
            <div className="text-right">
              <span className="text-gray-400 text-[10px] block">仓管费 ({storageFeeDisplay})</span>
              <span className="text-amber-600 text-sm font-bold">
                ¥{storageFee.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </span>
              <span className="text-gray-400 text-[10px] block mt-1">工费 ({craftFeeDisplay})</span>
              <span className="text-amber-600 text-sm font-bold">
                ¥{craftFee.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleDirectionClick('OUT')}
              disabled={orderBlocked}
              onPointerDown={() => setPressedSubmit(true)}
              onPointerUp={() => setPressedSubmit(false)}
              onPointerLeave={() => setPressedSubmit(false)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold flex flex-col items-center justify-center cursor-pointer transition-all duration-150 shadow-sm ${
                orderBlocked
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : agreed
                    ? 'bg-green-500 text-white active:scale-[0.98]'
                    : 'bg-brown-100 text-brown-600 active:scale-[0.98]'
              } ${pressedSubmit ? 'scale-[0.98]' : ''}`}
            >
              {!isLive ? (
                <span className="text-xs">行情未连接</span>
              ) : tradingStatus && tradingStatus.enabled && !tradingStatus.isOpen ? (
                <span className="text-xs">暂停交易</span>
              ) : agreed ? (
                <>
                  <span className="text-xs opacity-80">卖出</span>
                  <span className="text-lg font-bold leading-tight">
                    ¥{(selectedProduct.isKiloBar ? liveKiloBarBid : selectedProduct.type === 'silver' ? liveSilverBid : liveGoldBid).toLocaleString('zh-CN', { minimumFractionDigits: priceDigits, maximumFractionDigits: priceDigits })}
                  </span>
                </>
              ) : (
                <>
                  <i className="ri-file-list-3-line text-sm"></i>
                  <span className="text-xs">先同意协议</span>
                </>
              )}
            </button>
            <button
              onClick={() => handleDirectionClick('IN')}
              disabled={orderBlocked}
              onPointerDown={() => setPressedSubmit(true)}
              onPointerUp={() => setPressedSubmit(false)}
              onPointerLeave={() => setPressedSubmit(false)}
              className={`flex-1 py-2.5 rounded-lg text-sm font-semibold flex flex-col items-center justify-center cursor-pointer transition-all duration-150 shadow-sm ${
                orderBlocked
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : agreed
                    ? 'bg-red-500 text-white active:scale-[0.98]'
                    : 'bg-brown-100 text-brown-600 active:scale-[0.98]'
              } ${pressedSubmit ? 'scale-[0.98]' : ''}`}
            >
              {!isLive ? (
                <span className="text-xs">行情未连接</span>
              ) : tradingStatus && tradingStatus.enabled && !tradingStatus.isOpen ? (
                <span className="text-xs">暂停交易</span>
              ) : agreed ? (
                <>
                  <span className="text-xs opacity-80">买入</span>
                  <span className="text-lg font-bold leading-tight">
                    ¥{(selectedProduct.isKiloBar ? liveKiloBarAsk : selectedProduct.type === 'silver' ? liveSilverAsk : liveGoldAsk).toLocaleString('zh-CN', { minimumFractionDigits: priceDigits, maximumFractionDigits: priceDigits })}
                  </span>
                </>
              ) : (
                <>
                  <i className="ri-file-list-3-line text-sm"></i>
                  <span className="text-xs">先同意协议</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Order Confirm Modal */}
      {confirmModalOpen && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-end justify-center">
          <div className="bg-white rounded-t-2xl w-full p-5 sheet-slide-up max-w-lg">
            {/* Product & Price */}
            <div className="text-center mb-5">
              <h3 className="text-brown-900 text-base font-bold">{selectedProduct.name}</h3>
              <p
                className={`text-brown-950 text-3xl font-bold mt-2 transition-colors duration-300 ${
                  priceFlash === 'up' ? 'text-red-500 animate-flash-up' : ''
                } ${priceFlash === 'down' ? 'text-green-500 animate-flash-down' : ''}`}
              >
                ¥{currentPrice.toLocaleString('zh-CN', { minimumFractionDigits: priceDigits, maximumFractionDigits: priceDigits })}
              </p>
              <div className="flex items-center justify-center gap-1.5 mt-1">
                <p className="text-gray-400 text-xs">成交价格（元 / {selectedProduct.unit}）</p>
                {direction !== 'flat' && (
                  <span className={`text-[10px] font-bold px-1 py-0.5 rounded ${
                    direction === 'up' ? 'bg-red-50 text-red-500' : 'bg-green-50 text-green-500'
                  }`}>
                    {direction === 'up' ? '↑' : '↓'}
                    {Math.abs(changePct).toFixed(2)}%
                  </span>
                )}
              </div>
            </div>

            {/* Detail Rows */}
            <div className="bg-brown-50/60 rounded-xl p-4 space-y-3 mb-5">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">交易类型</span>
                <span className="text-brown-900 text-sm font-medium">{orderDirection === 'IN' ? '锁价买料' : '锁价卖料'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">商品规格</span>
                <span className="text-brown-900 text-sm font-medium">{weight}{selectedProduct.unit}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">实时单价</span>
                <span className="text-brown-900 text-sm font-medium">
                  ¥{currentPrice.toLocaleString('zh-CN', { minimumFractionDigits: priceDigits, maximumFractionDigits: priceDigits })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">预约总额</span>
                <span className="text-brown-900 text-sm font-bold">
                  ¥{totalAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">定金 ({selectedOption.label})</span>
                <span className="text-brown-900 text-sm font-bold">
                  ¥{depositAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">仓管费 ({storageFeeDisplay})</span>
                <span className="text-amber-600 text-sm font-bold">
                  ¥{storageFee.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">工费 ({craftFeeDisplay})</span>
                <span className="text-amber-600 text-sm font-bold">
                  ¥{craftFee.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">合计扣除</span>
                <span className="text-red-500 text-sm font-bold">
                  ¥{totalDeduct.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 text-sm">锁价周期</span>
                <span className="text-brown-900 text-sm font-medium">{currentLockPeriod}</span>
              </div>
            </div>

            {/* Buttons */}
            <button
              onClick={handleConfirmOrder}
              disabled={submitting}
              className={`w-full py-3.5 rounded-xl text-sm font-semibold cursor-pointer active:scale-[0.98] transition-all shadow-md mb-3 flex items-center justify-center gap-2 disabled:opacity-60 ${
                orderDirection === 'IN'
                  ? 'bg-red-500 text-white'
                  : 'bg-green-500 text-white'
              }`}
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  处理中...
                </>
              ) : (
                orderDirection === 'IN' ? '确认买入' : '确认卖出'
              )}
            </button>
            <button
              onClick={() => setConfirmModalOpen(false)}
              className="w-full py-3.5 rounded-xl bg-gray-100 text-brown-700 text-sm font-medium cursor-pointer active:scale-[0.98] transition-all"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* Risk Disclaimer Full-Screen Modal */}
      {riskModalOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-white animate-fade-up">
          {/* Modal Header with Progress */}
          <div className="sticky top-0 z-10 bg-white border-b border-gray-100">
            <div className="px-4 py-3 flex items-center justify-between">
              <button
                onClick={handleCloseRiskModal}
                className="w-8 h-8 flex items-center justify-center cursor-pointer"
              >
                <i className="ri-close-line text-brown-900 text-xl"></i>
              </button>
              <h2 className="text-brown-900 text-sm font-bold">{lockTerms.riskName}</h2>
              <div className="w-8" />
            </div>
            {/* Reading Progress Bar */}
            <div className="w-full h-1 bg-gray-100">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-gold-500 rounded-r-full transition-all duration-100"
                style={{ width: `${readProgress}%` }}
              />
            </div>
            <div className="px-4 py-1 flex items-center justify-between">
              <span className="text-gray-400 text-[10px]">阅读进度</span>
              <span className={`text-[10px] font-bold ${readProgress >= 95 ? 'text-green-500' : 'text-amber-600'}`}>
                {Math.round(readProgress)}%
              </span>
            </div>
          </div>

          {/* Scrollable Content */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4"
          >
            <div className="bg-amber-50 rounded-xl p-4 mb-4 border border-amber-100">
              <div className="flex items-center gap-2">
                <i className="ri-alarm-warning-line text-amber-600 text-lg"></i>
                <span className="text-amber-800 text-sm font-bold">重要提示</span>
              </div>
              <p className="text-amber-700 text-xs mt-2 leading-relaxed">
                为充分保障您的合法权益，请仔细阅读以下风险提示内容。您需要完整阅读至底部，方可确认已阅读并同意。
              </p>
            </div>

            {riskDisclaimerContent.map((section, idx) => (
              <div key={idx} className="mb-5">
                <h3 className="text-brown-900 text-sm font-bold mb-2 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {idx + 1}
                  </span>
                  {section.title}
                </h3>
                <p className="text-gray-600 text-xs leading-relaxed pl-7">
                  {section.body}
                </p>
              </div>
            ))}

            {/* Spacer for bottom button */}
            <div className="h-40" />
          </div>

          {/* Fixed Bottom Confirm Button */}
          <div className="sticky bottom-0 bg-white border-t border-gray-100 px-4 pt-3 pb-6">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-500 to-gold-500 rounded-full transition-all duration-100"
                  style={{ width: `${readProgress}%` }}
                />
              </div>
              <span className={`text-xs font-bold whitespace-nowrap ${readProgress >= 95 ? 'text-green-500' : 'text-gray-400'}`}>
                {readProgress >= 95 ? '已读完' : `${Math.round(readProgress)}%`}
              </span>
            </div>
            <button
              onClick={handleConfirmRiskRead}
              disabled={!canConfirmRisk}
              className={`w-full py-3.5 rounded-xl text-white text-base font-semibold flex items-center justify-center gap-2 cursor-pointer transition-all duration-150 ${
                canConfirmRisk
                  ? 'bg-gradient-to-r from-amber-600 to-amber-500 active:scale-[0.98]'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {canConfirmRisk ? (
                <>
                  <i className="ri-check-double-line"></i>
                  我已阅读并同意
                </>
              ) : (
                <>
                  <i className="ri-scroll-to-bottom-line"></i>
                  请继续阅读至底部
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}